import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Config } from '@app/model/config.model';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { removeEmptyProps } from '@app/util/common-utils';
import { ApiService } from '@app/services/api.service';
import { ToastService } from '@app/services/toast.service';
import { WfFeaturesService } from '@app/services/wf-features.service';
import { CommonFunctionalitiesService } from './common-functionalities.service';

/********************** Interface **********************/
interface Arguments {
  key: string;
  desc: string;
  from: string;
  name: string;
  default: string;
  value: any;
  required: boolean;
}

interface ToolAndThreshold {
  id: number;
  name: string;
  threshold: number;
  alwFail: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigBuilderService {

  /****************** Variable declaration ******************/
  targetExec: boolean = false; // Direct build execution via URL
  private triggerGetWfDetailsSubject = new Subject<void>();
  triggerGetWfDetails$ = this.triggerGetWfDetailsSubject.asObservable();
  private getWorkRepoNamesSubject = new Subject<void>();
  getWorkRepoNames$ = this.getWorkRepoNamesSubject.asObservable();
  private dataSubject = new BehaviorSubject<any>(null);
  data$: Observable<any> = this.dataSubject.asObservable();

  public configApis = this.apiService.CONFIG_BUILDER_APIS;
  public workflowApis = this.apiService.WORKFLOW_APIS;
  public githubApis = this.apiService.GITHUB_APIS;

  public configUrl = this.apiService.KPID_URL + this.configApis.WF_HUB;
  public wfEvntActUrl = this.apiService.KPID_URL + this.configApis.WF_EVENT_ACTION
  public configLabelUrl = this.apiService.KPID_URL + this.configApis.WF_CLOUD_ORCH;
  public workflowUrl =
    this.apiService.KPID_URL + this.workflowApis.WORKFLOW;
  public githubUrl = this.apiService.KPID_URL + this.githubApis.GITHUB;

  // GitHub issue template APIs
  public issueTmplApis = this.apiService.ISSUE_TMPL_APIS;
  public issueTmplUrl = this.apiService.KPID_URL + this.issueTmplApis.ISSUE_TMPL;

  public labelApis = this.apiService.LABEL_APIS;
  public labelUrl = this.apiService.KPID_URL + this.labelApis.LABEL;

  argumentsArray: Arguments[] = [];
  toolsAndThresholdArray: ToolAndThreshold[] = [];

  headers = new HttpHeaders({ "isLoader": "false" })

  threshHoldDefaultValues = {
    added: [],
    updated: [],
    deleted: []
  }

  /**
   * Constructor
   * @param httpClient 
   * @param service 
   * @param toastService 
   * @param apiService 
   * @param dialog 
   */
  constructor(private httpClient: HttpClient,
    private service: WfFeaturesService,
    private toastService: ToastService,
    private apiService: ApiService,
    private dialog: MatDialog
  ) { }

  getArgumentsArray() {
    return this.argumentsArray;
  }

  getToolsAndThresholdArray() {
    return this.toolsAndThresholdArray;
  }

  getConfigRepoData() {
    return this.argumentsArray;
  }

  setArgumentsArray(array: Arguments[]) {
    this.argumentsArray = array;
  }

  setToolsAndThresholdArray(array: ToolAndThreshold[]) {
    this.toolsAndThresholdArray = array;
  }

  /**
   * This function updates provided configuration
   * @param configObj updated configuration object 
   * @returns 
   */
  updateWorkflowConfig(disable: number, configObj: any, issueTmplId: number | null = null) {
    let payload: Config = {
      name: configObj.cngName,
      inpArgs: configObj.argumentsArray,
      brPtrn: configObj.branch,
      repoOwner: configObj.repoOwner,
      repoPtrn: configObj.repoPtrn,
      active: configObj.status,
      qgRule: configObj?.toolsAndThreshold || this.threshHoldDefaultValues,
      artifact: configObj.artifact,
      updQgCheck: configObj.updQgCheck,
      isSole: configObj.isSole,
      eventIds: configObj.eventIds,
      labelId: configObj.labelId,
    };
    // remove empty props 
    payload = removeEmptyProps(payload);

    if (payload.repoPtrn) {
      payload.repoPtrn = configObj.repoUrl ? null : configObj.repoPtrn;
    }

    // issue template id
    payload.issueTempId = issueTmplId;

    let onResponse: Promise<any> = new Promise<Config>((resolve, reject) => {
      try {
        this.service.updateWorkflowConfig(configObj.wfId, disable, payload).subscribe(
          (response) => {
            this.toastService.showSuccess('', 'Workflow configuration updated!');
            resolve(response.data.dbResp);
          },
          () => {
            this.toastService.showError('Failed', 'Failed to update Configuration!');
          }
        );
      }
      catch (error) {
        reject()
      };
    });

    return onResponse;
  }

  /**
   * Retrieve all available workflows from hub
   * @returns
   */
  getAvailableWorkflows(): Observable<any> {
    const url = this.configUrl + this.configApis.GET_WORKFLOWS;
    let response = this.httpClient.get(url);
    return response;
  }

  /**Retrieve all the workflows from hub with details like wfName, repoUrl, yml file url */
  getAllWorkflows() {
    const url = this.configUrl + this.configApis.GET_WF_DETAILS;
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieves input arguments of a specific workflow
   * @param wfId : Id of the workflow selected
   * @returns
   */
  getInputArguments(wfId: any): Observable<any> {
    const url = this.configUrl + this.configApis.GET_INPUT_ARGS(wfId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieves input arguments of a specific workflow and a specific job
   * @param jobId : jobId of the executed job
   * @returns
   */
  getInputArgsFromWfjobWfHub(jobId: any) {
    const url = this.configUrl + this.configApis.GET_INPUT_ARGS_FROM_WFHUB_WFJOB(jobId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieves quality gates of a specific workflow from wf_hub
   * @param wfhId : Id of the workflow selected
   * @returns
   */
  getQualityGates(wfhId: any): Observable<any> {
    const url = this.configUrl + this.configApis.GET_QUALITY_GATES(wfhId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Checks if a workflow configuration is active for a given repository and branch condition.
   * @param {any} workFlowObj - The workflow object containing repository and branch details.
   * @returns {Promise<number>} - A Promise that resolves to either the workflow ID or -1 if not active.
  */
  checkIsActive(wfhId: number, workFlowObj: any, currentWfId: number = -1): Promise<number> {
    // Construct the URL to check if the workflow is active
    const isWfActiveUrl = this.workflowUrl + this.workflowApis.WORKFLOW_IS_AV

    const params: any = {
      wfhId,
      brPtrn: workFlowObj.branch, event: workFlowObj.event.name,
      repoOwner: workFlowObj.repoOwner
    }

    if (workFlowObj?.repoPtrn) {
      params.repoPtrn = workFlowObj.repoPtrn
    }

    if (workFlowObj?.repoUrl) {
      params.repoUrl = workFlowObj.repoUrl
    }

    if ((workFlowObj?.action[0] != null) && (workFlowObj?.action[0] != undefined)) {
      params.eventActionIds = workFlowObj.action
    }

    return new Promise((resolve, reject) => {
      this.httpClient.post(isWfActiveUrl, params)
        .subscribe((resp: any) => {
          let activeId: number = resp.data.dbResp.filter((id: number) => id != currentWfId)[0];
          if (activeId) {
            // If the workflow is already active, show a confirmation dialog
            const dialogData = new ConfirmDialogModel("Configuration already active", "Configuration already active for this repository and branch. Do you want to make this configuration active");
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              width: "370px",
              disableClose: true,
              data: dialogData
            });
            dialogRef.afterClosed().subscribe((dialogResult: boolean) => {
              // If the user confirms, resolve with the workflow ID; otherwise, reject
              dialogResult === false ? reject() : resolve(activeId);
            });
          } else {
            // If the workflow is not active, resolve with -1
            resolve(-1);
          }
        },
          () => {
            // Handle errors and reject with -1
            this.toastService.showError('Failed', 'Failed to save configuration!')
            reject(-1);
          });
    })
  }

  /**
   * Calls API to insert a workflow along with its configuration
   * @param wfData : Configured data of a workflow
   * @returns
   */
  insertWorkflow(activeWf: number | null, configWf: any, wfhId: any, issueTmplId: number | null): Promise<any> {
    
    configWf.toolsAndThresholdArray = configWf.toolsAndThresholdArray.map((qg: any) => {
      delete qg.checkbox;
      return qg;
    });
    let payload: Config = {
      wfId: wfhId,
      name: configWf.cngName,
      repoOwner: configWf.repoOwner,
      qgRules: configWf.toolsAndThresholdArray,
      inpArgs: configWf.argumentsArray,
      repoPtrn: configWf.repoPtrn || null,
      brPtrn: configWf.branch,
      rpId: 8,
      artifact: configWf.artifact,
      updQgCheck: configWf.updQgCheck,
      isSole: configWf.isSole,
      eventIds: configWf.action[0] ? configWf.action : [],
      issueTempId: issueTmplId || null,
      labelId: configWf.labelId,
    };

    /**
    * Adds a workflow configuration to the service.
    * @param {Config} activeWf - The active workflow configuration.
    * @param {any} payload - The payload to send to the service.
    * @returns {Promise<Config>} - A Promise that resolves to the added workflow configuration or rejects on failure.
    */
    let onResponse: Promise<any> = new Promise<Config>((resolve, reject) => {
      try {
        // Send an HTTP POST request to add the workflow configuration
        this.service.addWorkflow(activeWf, payload).subscribe(
          (response) => {
            // On success, show a success toast message and resolve with the added configuration
            this.toastService.showSuccess(
              '',
              'Workflow configuration saved !!'
            )
            resolve(response.data.dbResp);
          },
          () => {
            // On failure, show an error toast message and reject
            this.toastService.showError('Failed', 'Failed to Save Configuration!')
            reject();
          }
        );
      } catch (error) {
        // Handle any exceptions and reject
        reject()
      }
    });

    return onResponse;
  }

  /**
   * Retrieves quality gates configured for a workflow
   * @param wfId : Id of the workflow selected
   * @returns
   */
  getWorkflowQualityGates(wfId: any) {
    const url = this.configUrl + this.configApis.GET_WF_QUALITY_GATES(wfId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieves list of configured workflows
   * @returns
   */
  async getWfDetails(id: number) {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_DETAILS(id);
    let response = await this.httpClient.get(url);
    return response;
  }

  /** get branch list */
  getBranchList(repoUrl: string): Observable<any> {
    let repoData: string[] = (repoUrl.endsWith('/') ? repoUrl.substring(0, repoUrl.length - 1) : repoUrl).split('/');
    const url =
      this.configUrl +
      this.configApis.GET_BRANCH_LIST(
        repoData[repoData.length - 1],
        repoData[repoData.length - 2]
      );

    let response = this.httpClient.get(url, { headers: this.headers });
    return response;
  }

  /** get workflow config details */
  getWorkflowConfigDetails(wfId: any) {
    const url = this.configUrl + this.configApis.GET_INPUT_ARGS(wfId);
    let response = this.httpClient.get(url);
    return response;
  }

  /** delete workflow config */
  deleteWorkflowConfig(wfId: number, wfName: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.service.deleteWorkflow(wfId).toPromise().then(
        (response) => {
          resolve(true)
          this.toastService.showSuccess(
            '',
            'Workflow configuration deleted !!'
          )
        }
      ).catch(() => {
        reject()
        this.toastService.showError('Failed', 'Failed to delete configuration!')
      });
    })
  }

  /**
   * @returns returns the mapped runner labels
   */
  getRunnerLabels(os: string, type: string) {
    return this.httpClient.get(this.configLabelUrl + this.configApis.WF_RUNNER_CLOUD_ORCH_LABELS(os, type)).pipe(map((data: any) => {
      if (data?.data?.length > 0) {
        return data?.data?.map((label: any) => {
          return { name: label.label, value: label.label_id }
        })
      }
      return [];
    }))
  }

  /********************Manual workflow trigger api's *****************************************/
  manualWfTrigger(param: any, branch: any, inputJson: any) {
    let url = this.configUrl + this.configApis.MANUAL_TRIGGER(param, branch);
    return this.httpClient.post(url, inputJson);
  }

  /**Api is used to change the active flag of wfh table */
  updateActiveFlg(wfhId: number, input: any) {
    return this.httpClient.patch(this.configUrl + this.configApis.WFH_UPDATE(wfhId), input);
  }

  /** get all events */
  getAllEvents() {
    return this.httpClient.get(`${this.apiService.KPID_URL}${this.configApis.GET_EVENTS}`);
  }

  /** get actions based on the event id */
  getActions(id: number) {
    return this.httpClient.get(`${this.wfEvntActUrl}${this.configApis.GET_ACTIONS(id)}`);
  }

  /**Get workflow based on condition */
  getWfOnCond(repoName: any, branch: any, workflow: any) {
    return this.httpClient.get(this.configUrl + this.configApis.GET_WF_ON_COND(repoName, branch, workflow));
  }

  /** Get workflow hub details using executed action url */
  getWfOnActionUrl(actionUrl: string) {
    return this.httpClient.get(this.configUrl + this.configApis.GET_WF_ACTION_URL(actionUrl));
  }

  /** triggerGetWfDetails is used to trigger getWfDetails api when user is updating a config */
  triggerGetWfDetails(event: any) {
    this.triggerGetWfDetailsSubject.next(event);
  }

  /** get workflow repo names */
  getWorkRepoNames(event: any) {
    this.getWorkRepoNamesSubject.next(event);
  }

  /** set data */
  setData(data: any) {
    this.dataSubject.next(data);
  }

  /** get data */
  getData() {
    return this.dataSubject.value;
  }

  /**
   * GitHub issue template APIs
   */

  /**Get issue template list */
  getIssueTmplList(repoUrl: string, branchName: string) {
    return this.httpClient.get(this.issueTmplUrl + this.issueTmplApis.GET_ISSUE_TMPL_LIST(repoUrl, branchName));
  }

  /** Get input arguments based on issue template id */
  getIssueTmplComp(id: number) {
    return this.httpClient.get(this.issueTmplUrl + this.issueTmplApis.ISSUE_TMPL_COMP(id));
  }

  /** Get all labeled checks */
  getLabelList() {
    return this.httpClient.get(this.labelUrl + this.labelApis.VIEW_ALL);
  }
}
